import { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  DollarSign, Search, Calendar, Download, List, LayoutGrid,
  ChevronLeft, ChevronRight, TrendingUp, AlertTriangle, CheckCircle, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, startOfDay, endOfDay, startOfYear, endOfYear, addMonths, subMonths } from 'date-fns';
import { usePermissions } from '../context/PermissionsContext';
import AccessDenied from '../components/AccessDenied';
import { logger } from '../utils/logger';
import { formatLocalDate } from '../utils/dateFormat';
import { fmtKPI } from '../utils/kpiFormat';
import { useKPIDefinition } from '../components/KPIDefinitionModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const METHODS = ['Cash', 'Bank', 'Card', 'Zelle', 'Other'];
const METHOD_LABELS = { Cash: 'Cash', Bank: 'Bank Transfer', Card: 'Card', Zelle: 'Zelle', Other: 'Other' };
const STATUS_COLORS = { pending: 'bg-blue-100 text-blue-800', completed: 'bg-emerald-100 text-emerald-800', overdue: 'bg-red-100 text-red-800' };

const fmt$ = fmtKPI;

// KPI Card for scheduled vs collected (clickable for definition modal)
const PayKPI = ({ title, scheduled, collected, icon: Icon, color, onClick }) => {
  const pct = scheduled > 0 ? Math.min((collected / scheduled) * 100, 100) : 0;
  return (
    <Card className={`overflow-hidden ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
      data-testid={`pay-kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className={`p-1.5 rounded-lg ${color}`}><Icon size={14} /></div>
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{title}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-bold text-slate-900">{fmt$(collected)}</span>
          <span className="text-xs text-slate-400">/ {fmt$(scheduled)}</span>
        </div>
        <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-[10px] text-slate-400 mt-1">{pct.toFixed(0)}% collected</p>
      </CardContent>
    </Card>
  );
};

export default function PaymentsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('list'); // list | calendar
  const [filterMode, setFilterMode] = useState('month'); // day | month | year
  const [filterDate, setFilterDate] = useState(new Date());
  const [accessDenied, setAccessDenied] = useState(false);
  const { hasPermission, loading: permLoading } = usePermissions();

  // Record Payment modal
  const [recordOpen, setRecordOpen] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [payForm, setPayForm] = useState({ contract_id: '', category: 'Installment', amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0], money_receipt_no: '', notes: '' });
  const [payLoading, setPayLoading] = useState(false);

  const canView = hasPermission('payments_view');
  const canCreate = hasPermission('payments_create');
  const { openKPI, modal: kpiModal } = useKPIDefinition();

  // Compute date range
  const dateRange = useMemo(() => {
    const d = filterDate;
    if (filterMode === 'day') return { start: startOfDay(d), end: endOfDay(d) };
    if (filterMode === 'year') return { start: startOfYear(d), end: endOfYear(d) };
    return { start: startOfMonth(d), end: endOfMonth(d) };
  }, [filterDate, filterMode]);

  const fetchData = useCallback(async () => {
    try {
      // Send naive YYYY-MM-DDTHH:MM:SS strings (no UTC suffix). toISOString()
      // shifts to UTC, which causes any month-end payment in a TZ west of UTC
      // to leak into the next month's bucket on the backend's string compare.
      const fmt = (d, h, m, s) => {
        const Y = d.getFullYear();
        const M = String(d.getMonth() + 1).padStart(2, '0');
        const D = String(d.getDate()).padStart(2, '0');
        const H = String(h).padStart(2, '0');
        const Mi = String(m).padStart(2, '0');
        const S = String(s).padStart(2, '0');
        return `${Y}-${M}-${D}T${H}:${Mi}:${S}`;
      };
      const params = new URLSearchParams();
      params.append('start_date', fmt(dateRange.start, 0, 0, 0));
      params.append('end_date', fmt(dateRange.end, 23, 59, 59));
      const r = await fetch(`${API_URL}/api/financial/payments-dashboard?${params}`, { credentials: 'include' });
      if (r.ok) setData(await r.json());
      else if (r.status === 403) setAccessDenied(true);
    } catch (e) { logger.error('Payments fetch error', e); }
    setLoading(false);
  }, [dateRange]);

  const fetchContracts = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/financial/contracts`, { credentials: 'include' });
      if (r.ok) setContracts(await r.json());
    } catch (err) {
      logger.error('PaymentsPage fetchContracts failed', err);
    }
  }, []);

  useEffect(() => {
    if (!permLoading) {
      if (!canView) { setAccessDenied(true); setLoading(false); }
      else { fetchData(); fetchContracts(); }
    }
  }, [permLoading, canView, fetchData, fetchContracts]);

  // Navigation
  const navigateDate = (dir) => {
    if (filterMode === 'day') setFilterDate(p => dir === 'next' ? new Date(p.getTime() + 86400000) : new Date(p.getTime() - 86400000));
    else if (filterMode === 'month') setFilterDate(p => dir === 'next' ? addMonths(p, 1) : subMonths(p, 1));
    else setFilterDate(p => new Date(p.getFullYear() + (dir === 'next' ? 1 : -1), 0, 1));
  };

  const dateLabel = filterMode === 'day' ? format(filterDate, 'MMM d, yyyy')
    : filterMode === 'year' ? format(filterDate, 'yyyy')
    : format(filterDate, 'MMMM yyyy');

  // Record payment
  const handleRecord = async () => {
    if (!payForm.contract_id || !payForm.amount) { toast.error('Contract and amount are required'); return; }
    setPayLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/financial/contracts/${payForm.contract_id}/payments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          amount: parseFloat(payForm.amount),
          payment_category: payForm.category,
          payment_method: payForm.payment_method,
          payment_date: payForm.payment_date,
          money_receipt_no: payForm.money_receipt_no || null,
          notes: payForm.notes || null,
        }),
      });
      if (r.ok) {
        toast.success('Payment recorded');
        setRecordOpen(false);
        setPayForm({ contract_id: '', category: 'Installment', amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0], money_receipt_no: '', notes: '' });
        fetchData();
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Failed to record payment');
      }
    } catch { toast.error('Failed to record payment'); }
    setPayLoading(false);
  };

  // Export CSV
  const exportCSV = () => {
    if (!data?.schedule?.length) { toast.error('No data to export'); return; }
    const headers = ['Client', 'Phone', 'Owner', 'Category', 'Amount', 'Due Date', 'Status', 'File ID'];
    const rows = data.schedule.map(s => [s.client_name, s.phone || '', s.owner, s.category, s.amount, s.due_date?.split('T')[0] || '', s.status, s.file_id]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `payments_${dateLabel.replace(/\s+/g, '_')}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  // Search filter
  const filteredSchedule = useMemo(() => {
    if (!data?.schedule) return [];
    if (!search.trim()) return data.schedule;
    const q = search.toLowerCase();
    return data.schedule.filter(s =>
      s.client_name?.toLowerCase().includes(q) ||
      s.phone?.toLowerCase().includes(q) ||
      s.owner?.toLowerCase().includes(q) ||
      s.file_id?.toLowerCase().includes(q) ||
      s.category?.toLowerCase().includes(q)
    );
  }, [data, search]);

  // Calendar view
  const calendarDays = useMemo(() => {
    if (filterMode !== 'month' || !data?.schedule) return [];
    const start = startOfMonth(filterDate);
    const daysInMonth = new Date(filterDate.getFullYear(), filterDate.getMonth() + 1, 0).getDate();
    const startDow = start.getDay();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push({ day: null, items: [] });
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${filterDate.getFullYear()}-${String(filterDate.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const items = data.schedule.filter(s => s.due_date?.startsWith(dateStr));
      cells.push({ day: d, items, dateStr });
    }
    return cells;
  }, [filterDate, filterMode, data]);

  if (loading || permLoading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold" /></div>;
  if (accessDenied) return <AccessDenied message="You don't have access to payments." />;

  const kpis = data?.kpis || {};
  const summary = data?.summary || {};

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payments</h1>
          <p className="text-sm text-slate-500">Track scheduled and collected payments</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv"><Download className="h-4 w-4 mr-1" /> Export</Button>
          {canCreate && <Button size="sm" onClick={() => setRecordOpen(true)} data-testid="record-payment-btn"><DollarSign className="h-4 w-4 mr-1" /> Record Payment</Button>}
        </div>
      </div>

      {/* Date Filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex bg-slate-100 rounded-lg p-0.5">
          {['day', 'month', 'year'].map(m => (
            <button key={m} className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${filterMode === m ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
              onClick={() => setFilterMode(m)} data-testid={`filter-${m}`}>{m.charAt(0).toUpperCase() + m.slice(1)}</button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateDate('prev')}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-semibold text-slate-700 min-w-[140px] text-center">{dateLabel}</span>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateDate('next')}><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <Button variant="ghost" size="sm" className="text-xs" onClick={() => setFilterDate(new Date())}>Today</Button>
      </div>

      {/* 6 KPI Cards (Total + 5 categories) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <PayKPI title="Total Collection" scheduled={kpis.total?.scheduled || 0} collected={kpis.total?.collected || 0} icon={DollarSign} color="bg-slate-100 text-slate-600"
          onClick={() => openKPI({
            title: 'Total Collection',
            value: `${fmt$(kpis.total?.collected || 0)} / ${fmt$(kpis.total?.scheduled || 0)}`,
            formula: 'SUM across all payment categories (Installment + Booking Money + Down Payment + Future Adjustment + One-time + Other)',
            description: 'Sum of every payment category for the selected date range. Scheduled reflects what is due from active contracts in range. Collected reflects actual payments recorded (non-voided) in range.',
            inputs: [
              { label: 'Booking Money', value: `${fmt$(kpis.booking_money?.collected || 0)} / ${fmt$(kpis.booking_money?.scheduled || 0)}` },
              { label: 'Down Payment', value: `${fmt$(kpis.downpayment?.collected || 0)} / ${fmt$(kpis.downpayment?.scheduled || 0)}` },
              { label: 'Installment', value: `${fmt$(kpis.installment?.collected || 0)} / ${fmt$(kpis.installment?.scheduled || 0)}` },
              { label: 'Future Adjustment', value: `${fmt$(kpis.future_adjustment?.collected || 0)} / ${fmt$(kpis.future_adjustment?.scheduled || 0)}` },
              { label: 'One-time', value: `${fmt$(kpis.one_time?.collected || 0)} / ${fmt$(kpis.one_time?.scheduled || 0)}` },
            ],
            source: 'Financial contracts + Payments collection',
          })}
        />
        <PayKPI title="Booking Money" scheduled={kpis.booking_money?.scheduled || 0} collected={kpis.booking_money?.collected || 0} icon={DollarSign} color="bg-amber-50 text-amber-600"
          onClick={() => openKPI({
            title: 'Booking Money',
            value: `${fmt$(kpis.booking_money?.collected || 0)} / ${fmt$(kpis.booking_money?.scheduled || 0)}`,
            formula: 'Scheduled: SUM(contract.booking_money_amount) for active contracts whose booking_money_date is in range.  Collected: SUM(payments.amount) WHERE payment_category = "Booking Money" AND payment_date in range.',
            description: 'Initial refundable or non-refundable amount that books a unit. Separate from Down Payment.',
            source: 'Financial contracts + Payments collection',
          })}
        />
        <PayKPI title="Down Payment" scheduled={kpis.downpayment?.scheduled || 0} collected={kpis.downpayment?.collected || 0} icon={DollarSign} color="bg-blue-50 text-blue-600"
          onClick={() => openKPI({
            title: 'Down Payment',
            value: `${fmt$(kpis.downpayment?.collected || 0)} / ${fmt$(kpis.downpayment?.scheduled || 0)}`,
            formula: 'Scheduled: SUM(contract.downpayment_amount) for active contracts whose downpayment_date is in range.  Collected: SUM(payments.amount) WHERE payment_category = "Down Payment" AND payment_date in range.',
            description: 'Upfront amount paid when the customer commits to the installment plan.',
            source: 'Financial contracts + Payments collection',
          })}
        />
        <PayKPI title="Future Adj." scheduled={kpis.future_adjustment?.scheduled || 0} collected={kpis.future_adjustment?.collected || 0} icon={TrendingUp} color="bg-purple-50 text-purple-600"
          onClick={() => openKPI({
            title: 'Future Adjustment',
            value: `${fmt$(kpis.future_adjustment?.collected || 0)} / ${fmt$(kpis.future_adjustment?.scheduled || 0)}`,
            formula: 'Scheduled: SUM of any contract.future_adjustments[] whose date is in range.  Collected: SUM(payments.amount) WHERE payment_category ∈ {"future_adjustment", "adjustment"}.',
            description: 'Lump-sum or special adjustment amounts planned against a contract (e.g., year-end top-ups).',
            source: 'Financial contracts.future_adjustments + Payments',
          })}
        />
        <PayKPI title="Installment" scheduled={kpis.installment?.scheduled || 0} collected={kpis.installment?.collected || 0} icon={Calendar} color="bg-emerald-50 text-emerald-600"
          onClick={() => openKPI({
            title: 'Installment',
            value: `${fmt$(kpis.installment?.collected || 0)} / ${fmt$(kpis.installment?.scheduled || 0)}`,
            formula: 'Scheduled: SUM(installments.due_amount) for all active contracts whose due_date is in range.  Collected: SUM(payments.amount) WHERE payment_category = "Installment" OR payment is linked to an installment_id.',
            description: 'Scheduled monthly obligations from multi-month plans. Excludes one-time contracts and non-installment categories.',
            source: 'Installments + Payments collection',
          })}
        />
        <PayKPI title="One-time" scheduled={kpis.one_time?.scheduled || 0} collected={kpis.one_time?.collected || 0} icon={DollarSign} color="bg-rose-50 text-rose-600"
          onClick={() => openKPI({
            title: 'One-time',
            value: `${fmt$(kpis.one_time?.collected || 0)} / ${fmt$(kpis.one_time?.scheduled || 0)}`,
            formula: 'Scheduled: SUM(contract.one_time_amount OR net_price) WHERE contract_type = "one_time" AND one_time_payment_date in range.  Collected: SUM(payments.amount) WHERE payment_category ∈ {"one-time", "one_time"}.',
            description: 'Full-payment contracts that skip the installment/booking/down-payment flow. These are single-shot settlements: scheduled on the contract\'s one-time payment date, and counted as collected when a matching one-time payment is recorded.',
            source: 'Financial contracts (contract_type=one_time) + Payments',
          })}
        />
        {(kpis.other?.collected > 0) && (
          <PayKPI title="Other" scheduled={0} collected={kpis.other?.collected || 0} icon={DollarSign} color="bg-slate-100 text-slate-500"
            onClick={() => openKPI({
              title: 'Other (Uncategorized)',
              value: fmt$(kpis.other?.collected || 0),
              formula: 'SUM(payments.amount) WHERE payment_category does not match a recognized bucket',
              description: `${kpis.other?.count || 0} payment(s) were recorded without a category that maps cleanly to Booking Money / Down Payment / Installment / Future Adjustment / One-time. These still count toward Total Collection so the totals reconcile — but they should be re-categorised in the Payments log to surface in the right KPI card.`,
              source: 'Payments collection (payment_category not mapped)',
            })}
          />
        )}
      </div>

      {/* Summary + Search + View Toggle */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          <Badge variant="outline" className="py-1 px-2"><Clock className="h-3 w-3 mr-1 inline" />{summary.pending || 0} Pending</Badge>
          <Badge variant="outline" className="py-1 px-2 text-emerald-700 border-emerald-200"><CheckCircle className="h-3 w-3 mr-1 inline" />{summary.collected || 0} Collected</Badge>
          <Badge variant="outline" className="py-1 px-2 text-red-700 border-red-200"><AlertTriangle className="h-3 w-3 mr-1 inline" />{summary.overdue || 0} Overdue</Badge>
        </div>
        <div className="flex-1 min-w-[180px] relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
          <Input placeholder="Search client, phone, file ID..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-9 text-sm" data-testid="search-payments" />
        </div>
        <div className="flex bg-slate-100 rounded-lg p-0.5">
          <button className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow' : ''}`} onClick={() => setViewMode('list')} data-testid="view-list"><List className="h-4 w-4" /></button>
          <button className={`p-1.5 rounded ${viewMode === 'calendar' ? 'bg-white shadow' : ''}`} onClick={() => setViewMode('calendar')} data-testid="view-calendar"><LayoutGrid className="h-4 w-4" /></button>
        </div>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>File ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSchedule.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">No scheduled payments for this period</TableCell></TableRow>
                  ) : filteredSchedule.map(s => (
                    <TableRow key={s.id} data-testid={`payment-row-${s.id}`}>
                      <TableCell className="font-medium text-sm">{s.client_name}</TableCell>
                      <TableCell className="text-xs">{s.phone || '-'}</TableCell>
                      <TableCell className="text-xs text-slate-600">{s.owner}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{s.category}</Badge></TableCell>
                      <TableCell className="font-semibold">${s.amount.toLocaleString()}</TableCell>
                      <TableCell className="text-sm">{s.due_date ? (() => {
                        // Parse calendar day directly to avoid TZ shift on pure-date strings
                        const m = String(s.due_date).match(/^(\d{4})-(\d{2})-(\d{2})/);
                        if (!m) return formatLocalDate(s.due_date);
                        const [, y, mo, d] = m;
                        return format(new Date(Number(y), Number(mo) - 1, Number(d)), 'MMM d, yyyy');
                      })() : '-'}</TableCell>
                      <TableCell><Badge className={`text-xs capitalize ${STATUS_COLORS[s.status] || ''}`}>{s.status}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-500">{s.file_id}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && filterMode === 'month' && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-7 gap-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className="text-center text-xs font-semibold text-slate-400 py-1">{d}</div>
              ))}
              {calendarDays.map((cell, i) => (
                <div key={`day-${cell.day || `pad-${i}`}`} className={`min-h-[80px] border rounded-lg p-1 ${cell.day ? 'bg-white' : 'bg-slate-50'}`}>
                  {cell.day && (
                    <>
                      <div className="text-xs font-medium text-slate-600 mb-0.5">{cell.day}</div>
                      <div className="space-y-0.5">
                        {cell.items.slice(0, 3).map(s => (
                          <div key={s.id} className={`text-[9px] px-1 py-0.5 rounded truncate ${s.status === 'overdue' ? 'bg-red-50 text-red-700' : s.status === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                            ${s.amount.toLocaleString()} - {s.client_name?.split(' ')[0]}
                          </div>
                        ))}
                        {cell.items.length > 3 && <div className="text-[9px] text-slate-400">+{cell.items.length - 3} more</div>}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      {viewMode === 'calendar' && filterMode !== 'month' && (
        <Card><CardContent className="p-8 text-center text-slate-400">Calendar view is available in Month mode. Switch to Month filter.</CardContent></Card>
      )}

      {/* Record Payment Dialog */}
      <Dialog open={recordOpen} onOpenChange={setRecordOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Contract *</Label>
              <Select value={payForm.contract_id} onValueChange={v => setPayForm(p => ({ ...p, contract_id: v }))}>
                <SelectTrigger data-testid="pay-contract"><SelectValue placeholder="Select contract" /></SelectTrigger>
                <SelectContent>
                  {contracts.filter(c => ['Active', 'Overdue'].includes(c.payment_status)).map(c => (
                    <SelectItem key={c.contract_id} value={c.contract_id}>{c.file_id} - {c.customer_name || 'Unknown'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={payForm.category} onValueChange={v => setPayForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Installment">Installment</SelectItem>
                    <SelectItem value="Booking Money">Booking Money</SelectItem>
                    <SelectItem value="Down Payment">Down Payment</SelectItem>
                    <SelectItem value="Partial">Partial</SelectItem>
                    <SelectItem value="One-time">One-Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount ($) *</Label>
                <Input type="number" step="0.01" value={payForm.amount} onChange={e => setPayForm(p => ({ ...p, amount: e.target.value }))} data-testid="pay-amount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Method</Label>
                <Select value={payForm.payment_method} onValueChange={v => setPayForm(p => ({ ...p, payment_method: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map(m => <SelectItem key={m} value={m}>{METHOD_LABELS[m]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Date</Label>
                <Input type="date" value={payForm.payment_date} onChange={e => setPayForm(p => ({ ...p, payment_date: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Receipt No.</Label>
              <Input value={payForm.money_receipt_no} onChange={e => setPayForm(p => ({ ...p, money_receipt_no: e.target.value }))} placeholder="Optional" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={payForm.notes} onChange={e => setPayForm(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecordOpen(false)}>Cancel</Button>
            <Button onClick={handleRecord} disabled={payLoading} data-testid="pay-submit">{payLoading ? 'Recording...' : 'Record Payment'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {kpiModal}
    </div>
  );
}
